import { getBlob, getJson } from './restClient';
import type {
  AnalysisAuditDetail,
  AnalysisForwardCandles,
  AnalysisAuditListResponse,
  AnalysisHistoryFilters,
  HistorySortKey,
  SortDir,
} from 'types/history';

export interface ListAnalysesParams extends AnalysisHistoryFilters {
  limit?: number;
  offset?: number;
  sortBy?: HistorySortKey | null;
  sortDir?: SortDir;
  /** The rule set a `verdict` / `grade` filter is scoped to. */
  window?: number;
  closeWindow?: number;
  stopMult?: number;
  replayVersion?: number;
}

/**
 * Query string for the shared filters.
 *
 * The single place filters are serialised: the listing, the Excel export and the
 * backtest stats all call it, so the workbook and the summary bar always cover
 * exactly what the table is showing. Add a filter here and all three get it.
 */
export function filterQuery(filters: AnalysisHistoryFilters): URLSearchParams {
  const qs = new URLSearchParams();
  if (filters.asset) qs.set('asset', filters.asset);
  // Repeated key rather than a joined value — the API reads ?engine=a&engine=b,
  // and an engine name is free-form enough that a delimiter could collide.
  for (const engine of filters.engine ?? []) qs.append('engine', engine);
  if (filters.status) qs.set('status', filters.status);
  if (filters.exchange) qs.set('exchange', filters.exchange);
  if (filters.market) qs.set('market', filters.market);
  if (filters.timeframe) qs.set('timeframe', filters.timeframe);
  if (filters.userId) qs.set('user_id', filters.userId);
  if (filters.side) qs.set('side', filters.side);
  if (filters.trend) qs.set('trend', filters.trend);
  if (filters.verdict) qs.set('verdict', filters.verdict);
  if (filters.grade) qs.set('grade', filters.grade);
  if (filters.since) qs.set('since', filters.since);
  if (filters.until) qs.set('until', filters.until);
  // Only sent when it differs from the server default, so the common URL stays
  // clean and an unset filter can never accidentally widen a listing.
  if (filters.forks && filters.forks !== 'exclude') qs.set('forks', filters.forks);
  return qs;
}

/**
 * Verdict and grade only mean something against a specific sweep, since both are
 * stored per rule set — send the rule set alongside them. Skipped when neither
 * is set, so the common case keeps a clean URL.
 */
export function withBacktestScope(
  qs: URLSearchParams,
  filters: AnalysisHistoryFilters,
  rules: { window?: number; closeWindow?: number; stopMult?: number; replayVersion?: number },
): void {
  if (!filters.verdict && !filters.grade) return;
  if (rules.window != null) qs.set('window', String(rules.window));
  // 0 is meaningful (time stop off), so compare against null rather than falsy.
  if (rules.closeWindow != null) qs.set('close_window', String(rules.closeWindow));
  if (rules.stopMult != null) qs.set('stop_mult', String(rules.stopMult));
  if (rules.replayVersion != null) qs.set('replay_version', String(rules.replayVersion));
}

/**
 * A page of persisted analyses (admin-only on the backend). Sorting is applied
 * server-side over the whole filtered set, so "most tokens" means most overall,
 * not most on this page.
 */
export function listAnalyses(
  params: ListAnalysesParams = {},
  signal?: AbortSignal,
): Promise<AnalysisAuditListResponse> {
  const qs = filterQuery(params);
  withBacktestScope(qs, params, params);
  if (params.limit != null) qs.set('limit', String(params.limit));
  if (params.offset != null) qs.set('offset', String(params.offset));
  if (params.sortBy) {
    qs.set('sort_by', params.sortBy);
    qs.set('sort_dir', params.sortDir ?? 'desc');
  }
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return getJson<AnalysisAuditListResponse>(`/analyses${suffix}`, signal);
}

/**
 * Download every analysis matching `filters` (not just the visible page) as a
 * multi-sheet .xlsx built server-side. Returns the blob plus the server's
 * suggested filename.
 */
export function exportAnalyses(
  filters: AnalysisHistoryFilters = {},
  pair: { window?: number; closeWindow?: number } = {},
  signal?: AbortSignal,
): Promise<{ blob: Blob; filename: string | null }> {
  const qs = filterQuery(filters);
  withBacktestScope(qs, filters, pair);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return getBlob(`/analyses/export${suffix}`, signal);
}

/** The candles after an analysis: the post-cutoff stub of the bar it ran in,
 *  then `count` on its timeframe — the server's replay horizon when `count` is
 *  omitted, so the export holds exactly what the verdicts were computed on —
 *  plus the same span `lowerSteps` timeframes lower. */
export function getAnalysisCandles(
  id: string,
  { count, lowerSteps = 2 }: { count?: number; lowerSteps?: number } = {},
  signal?: AbortSignal,
): Promise<AnalysisForwardCandles> {
  const qs = new URLSearchParams({ lower_steps: String(lowerSteps) });
  if (count != null) qs.set('count', String(count));
  return getJson<AnalysisForwardCandles>(`/analyses/${id}/candles?${qs.toString()}`, signal);
}

/** Full record (input trace + output + error) for one analysis. */
export function getAnalysis(id: string, signal?: AbortSignal): Promise<AnalysisAuditDetail> {
  return getJson<AnalysisAuditDetail>(`/analyses/${id}`, signal);
}
